/***
 *
 *   ONBOARDING
 *   Flow to help users set up the app, accepts multiple views/steps
 *   On finish/cancel, the user will be marked as onboarded
 *
 *   PROPS
 *   views: array of views to render containing keys:
 *     name, description and component to render
 *
 **********/

import axios from 'axios';
import { FieldInput } from 'components/fieldInput';
import {
  Animate,
  Button,
  CheckList,
  Logo,
  ViewContext,
} from 'components/lib';
import { stateOptions } from 'data';
import { Form, Formik } from 'formik';
import {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import * as Yup from 'yup';
import loader from './images/loading.gif';
import Style from './onboarding.module.scss';
import InputUtils from 'helpers/inputUtils';

export function Onboarding(props) {
  const context = useContext(ViewContext);
  const sigPad = useRef({});

  const [stage, setStage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');

  const [agencyData, setAgencyData] = useState({});

  const companyInfoValidation = Yup.object().shape({
    name: Yup.string().required('Field required!'),
    address1: Yup.string().required('Field required!'),
    address2: Yup.string(),
    city: Yup.string().required('Field required!'),
    zip: Yup.string().required('Field required!'),
    certificate_email: Yup.string()
      .email('Invalid email')
      .required('Field required!'),
    certificate_contact: Yup.string().required('Field required!'),
    certificate_phone: Yup.string().required('Field required!'),
  });

  function dataURItoBlob(dataURI) {
    var binary = atob(dataURI.split(',')[1]);
    var array = [];
    for (var i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: 'image/png' });
  }

  async function finish() {
    setLoading(true);
    await axios.patch('/api/user', { onboarded: true });

    const form_data = new FormData();
    form_data.append(
      'file',
      dataURItoBlob(
        sigPad.current.getTrimmedCanvas().toDataURL('image/png')
      )
    );

    await axios
      .post(`/api/user/signature`, form_data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((r) => {
        setLoading(false);

        context.notification.show(
          'Onboarding finished successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
      })
      .catch((fileErr) => {
        setLoading(false);

        context.notification.show(
          'Failed on upload file to server.',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });

    props.onFinish();
  }

  async function skip() {
    props.onSkip();

    await axios.patch('/api/user', { onboarded: true });
  }

  async function runAgencyAPI() {
    setLoading(true);

    await axios
      .get('/api/account')
      .then((r) => {
        setAgencyData(r.data.data);

        setPhone(
          inputUtils.number(
            r.data.data.certificate_phone
              ? r.data.data.certificate_phone
              : '',
            phone
          )
        );

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);

        context.notification.show(
          'Failed to load company infos',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  useEffect(() => {
    runAgencyAPI();
  }, []);

  const inputUtils = new InputUtils();
  const handleChangePhoneNumber = (event, handleChange) => {
    handleChange(event);
    setPhone(inputUtils.number(event.target.value, phone));
  };

  return (
    <Animate type="pop-center">
      <div className={Style.onboarding}>
        <section className={Style.sidebar}>
          <Logo className={Style.logo} />

          <CheckList
            color="white"
            className={Style.checklist}
            hideCross
            interactive
            items={[
              {
                name: 'Company info',
                checked: stage >= 1,
              },
              /*        {
                  name: "Documents",
                  checked: stage > 1,
                }, */
              {
                name: 'E-signature',
                checked: stage > 2,
              },
            ]}
          />
        </section>

        <section className={Style.main}>
          <header className={Style.header}>
            <h2 className={Style.name}>
              {stage === 1 &&
                'Please fill out your company information:'}
              {stage === 2 && "Upload company's documents"}
              {stage === 3 && 'Register your e-signature'}
            </h2>

            <span className={Style.description}>
              {stage === 1 && 'All the below are required fields.'}
              {stage === 2 && 'Insert your documents below'}
              {stage === 3 && 'Draw your signature below'}
            </span>
          </header>

          {stage === 1 && (
            <>
              {!loading ? (
                <>
                  <article className={Style.article}>
                    <Formik
                      validationSchema={companyInfoValidation}
                      initialValues={{
                        name:
                          agencyData && agencyData.name
                            ? agencyData.name
                            : '',
                        address1:
                          agencyData && agencyData.address1
                            ? agencyData.address1
                            : '',
                        address2:
                          agencyData && agencyData.address2
                            ? agencyData.address2
                            : '',
                        city:
                          agencyData && agencyData.city
                            ? agencyData.city
                            : '',
                        state:
                          agencyData && agencyData.state
                            ? agencyData.state
                            : stateOptions[0].value,
                        zip:
                          agencyData && agencyData.zip
                            ? agencyData.zip
                            : '',
                        certificate_contact:
                          agencyData && agencyData.certificate_contact
                            ? agencyData.certificate_contact
                            : '',
                        certificate_phone:
                          agencyData && agencyData.certificate_phone
                            ? agencyData.certificate_phone
                            : '',
                        certificate_email:
                          agencyData && agencyData.certificate_email
                            ? agencyData.certificate_email
                            : '',
                      }}
                      onSubmit={async (values) => {
                        setLoading(true);

                        values.certificate_contacts = [
                          {
                            name: values.certificate_contact,
                            email: values.certificate_email,
                            phone: values.certificate_phone,
                          },
                        ];

                        delete values.certificate_email;
                        delete values.certificate_phone;
                        delete values.certificate_contact;

                        await axios
                          .patch('/api/account', values)
                          .then((res) => {
                            setStage(3);
                            setLoading(false);
                          })
                          .catch((err) => {
                            setLoading(false);

                            context.notification.show(
                              'Failed to update company infos',
                              'error',
                              true,
                              'toast',
                              'x-circle'
                            );
                          });
                      }}
                    >
                      {({
                        errors,
                        touched,
                        values,
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        submitForm,
                      }) => (
                        <Fragment>
                          <Form
                            className={Style.form}
                            onSubmit={handleSubmit}
                          >
                            <div className={Style.fieldRow}>
                              <FieldInput
                                id="name"
                                type="text"
                                label="Company Name"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.name}
                                touched={touched.name}
                                disabled
                              />
                              <FieldInput
                                id="address1"
                                type="text"
                                label="Address 1"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.address1}
                                touched={touched.address1}
                                disabled
                              />
                            </div>
                            <div className={Style.fieldRow}>
                              <FieldInput
                                id="address2"
                                type="text"
                                label="Address 2"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.address2}
                                touched={touched.address2}
                                disabled
                              />
                              <FieldInput
                                id="city"
                                type="text"
                                label="City"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.city}
                                touched={touched.city}
                                disabled
                              />
                            </div>
                            <div className={Style.fieldRow}>
                              <FieldInput
                                id="state"
                                type="select"
                                label="State"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.state}
                                touched={touched.state}
                                options={stateOptions}
                                disabled
                              />
                              <FieldInput
                                id="zip"
                                type="text"
                                label="Zip"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.zip}
                                touched={touched.zip}
                                disabled
                              />
                            </div>
                            <div className={Style.fieldRow}>
                              <FieldInput
                                id="certificate_contact"
                                type="text"
                                label="Certificate Contact"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.certificate_contact}
                                touched={touched.certificate_contact}
                              />
                              <FieldInput
                                id="certificate_phone"
                                type="phone"
                                label="Certificate Phone"
                                handleChange={(event) => {
                                  handleChangePhoneNumber(
                                    event,
                                    handleChange
                                  );
                                }}
                                value={phone}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.certificate_phone}
                                touched={touched.certificate_phone}
                              />
                            </div>
                            <div className={Style.fieldRow}>
                              <FieldInput
                                id="certificate_email"
                                type="text"
                                label="Certificate Email"
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.certificate_email}
                                touched={touched.certificate_email}
                              />
                            </div>
                          </Form>
                          <div className={Style.footer}>
                            <div className={Style.buttons}>
                              <button
                                className={Style.save}
                                onClick={submitForm}
                                style={
                                  loading
                                    ? { cursor: 'default' }
                                    : { cursor: 'pointer' }
                                }
                              >
                                {loading ? (
                                  <img
                                    src={loader}
                                    alt="loader"
                                    style={{ width: 25 }}
                                  />
                                ) : (
                                  'Next'
                                )}
                              </button>
                            </div>
                          </div>
                        </Fragment>
                      )}
                    </Formik>
                  </article>
                </>
              ) : (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={loader}
                    alt="Loader"
                    style={{
                      width: 20,
                      height: 20,
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/*    {stage === 2 && (
            <>
              {!loading ? (
                <>
                  <article className={Style.article}>
                    <Formik
                      initialValues={{
                        w9_form:
                          agencyData && agencyData.w9_form
                            ? agencyData.w9_form
                            : [],
                        agency_license_and_individual_license:
                          agencyData &&
                          agencyData.agency_license_and_individual_license
                            ? agencyData.agency_license_and_individual_license
                            : [],
                        errors_and_omissions:
                          agencyData && agencyData.errors_and_omissions
                            ? agencyData.errors_and_omissions
                            : [],
                      }}
                      onSubmit={async (values, { setErrors }) => {
                        setLoading(true);

                        var errors = {};

                        //Validation

                        if (values.w9_form.length === 0) {
                          errors = {
                            ...errors,
                            w9_form: "Upload at least one file.",
                          };

                          setErrors(errors);
                        }

                        if (
                          values.agency_license_and_individual_license
                            .length === 0
                        ) {
                          errors = {
                            ...errors,
                            agency_license_and_individual_license:
                              "Upload at least one file.",
                          };

                          setErrors(errors);
                        }

                        if (values.errors_and_omissions.length === 0) {
                          errors = {
                            ...errors,
                            errors_and_omissions: "Upload at least one file.",
                          };

                          setErrors(errors);
                        }

                        if (
                          errors.errors_and_omissions ||
                          errors.w9_form ||
                          errors.agency_license_and_individual_license
                        ) {
                          return;
                        }

                        //Form Datas

                        const w9_form_form_data = new FormData();
                        const errors_and_omissions_form_data = new FormData();
                        const agency_license_and_individual_license_form_data =
                          new FormData();

                        //Appending all files

                        for (let i = 0; i < values.w9_form.length; i++) {
                          w9_form_form_data.append("files", values.w9_form[i]);
                        }

                        for (
                          let i = 0;
                          i < values.errors_and_omissions.length;
                          i++
                        ) {
                          errors_and_omissions_form_data.append(
                            "files",
                            values.errors_and_omissions[i]
                          );
                        }

                        for (
                          let i = 0;
                          i <
                          values.agency_license_and_individual_license.length;
                          i++
                        ) {
                          agency_license_and_individual_license_form_data.append(
                            "files",
                            values.agency_license_and_individual_license[i]
                          );
                        }

                        //Sending to server

                        const w9_form_upload = await axios.post(
                          "/api/account/upload/w9_form",
                          w9_form_form_data,
                          {
                            headers: {
                              "Content-Type": "multipart/form-data",
                            },
                          }
                        );

                        const errors_and_omissions_upload = await axios.post(
                          "/api/account/upload/errors_and_omissions",
                          errors_and_omissions_form_data,
                          {
                            headers: {
                              "Content-Type": "multipart/form-data",
                            },
                          }
                        );

                        const agency_license_and_individual_license_upload =
                          await axios.post(
                            "/api/account/upload/agency_license_and_individual_license",
                            agency_license_and_individual_license_form_data,
                            {
                              headers: {
                                "Content-Type": "multipart/form-data",
                              },
                            }
                          );

                        if (
                          w9_form_upload.status === 200 &&
                          agency_license_and_individual_license_upload.status ===
                            200 &&
                          errors_and_omissions_upload.status === 200
                        ) {
                          setLoading(false);
                          setStage(3);
                        }
                      }}
                    >
                      {({
                        errors,
                        touched,
                        values,
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        submitForm,
                        setFieldValue,
                      }) => (
                        <Fragment>
                          <Form className={Style.form} onSubmit={handleSubmit}>
                            <div className={Style.fieldRow}>
                              <FieldInput
                                id="w9_form"
                                type="upload_multiple"
                                label="W9 Form"
                                multiple
                                handleChange={(files) => {
                                  handleChange("w9_form");
                                  setFieldValue("w9_form", files.files);
                                }}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.w9_form}
                                touched={touched.w9_form}
                                required
                              />
                            </div>
                            <div
                              className={Style.fieldRow}
                              style={{ marginTop: 70, marginBottom: 70 }}
                            >
                              <FieldInput
                                id="agency_license_and_individual_license"
                                type="upload_multiple"
                                label="Copy of current state agency license and individual producer license"
                                handleChange={(files) => {
                                  handleChange(
                                    "agency_license_and_individual_license"
                                  );
                                  setFieldValue(
                                    "agency_license_and_individual_license",
                                    files.files
                                  );
                                }}
                                handleBlur={handleBlur}
                                showErrors
                                errors={
                                  errors.agency_license_and_individual_license
                                }
                                touched={
                                  touched.agency_license_and_individual_license
                                }
                                required
                              />
                            </div>
                            <div
                              className={Style.fieldRow}
                              style={{ marginTop: 60, marginBottom: 60 }}
                            >
                              <FieldInput
                                id="errors_and_omissions"
                                type="upload_multiple"
                                label="Copy of current Errors & Omissions"
                                handleChange={(files) => {
                                  handleChange("errors_and_omissions");
                                  setFieldValue(
                                    "errors_and_omissions",
                                    files.files
                                  );
                                }}
                                handleBlur={handleBlur}
                                showErrors
                                errors={errors.errors_and_omissions}
                                touched={touched.errors_and_omissions}
                                required
                              />
                            </div>
                          </Form>
                          <div className={Style.footer}>
                            <div className={Style.buttons}>
                              <button
                                className={Style.save}
                                onClick={!loading ? submitForm : null}
                                style={
                                  loading
                                    ? { cursor: "default" }
                                    : { cursor: "pointer" }
                                }
                              >
                                {loading ? (
                                  <img
                                    src={loader}
                                    alt="loader"
                                    style={{ width: 25 }}
                                  />
                                ) : (
                                  "Next"
                                )}
                              </button>
                            </div>
                          </div>
                        </Fragment>
                      )}
                    </Formik>
                  </article>
                </>
              ) : (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={loader}
                    alt="Loader"
                    style={{
                      width: 20,
                      height: 20,
                    }}
                  />
                </div>
              )}
            </>
          )} */}

          {stage === 3 && (
            <>
              <article className={Style.article}>
                {props.signature ? (
                  <>
                    <span className={Style.description}>
                      Current e-signature:
                    </span>
                    <div
                      style={{
                        backgroundImage:
                          'url(' + props.signature + ')',
                        width: 455,
                        height: 200,
                        marginTop: 20,
                      }}
                    />
                    <span className={Style.description}>
                      New e-signature:
                    </span>
                    <ReactSignatureCanvas
                      ref={(ref) => {
                        sigPad.current = ref;
                      }}
                      canvasProps={{
                        width: 455,
                        height: 200,
                        className: Style.canvas,
                      }}
                    />
                  </>
                ) : (
                  <ReactSignatureCanvas
                    ref={(ref) => {
                      sigPad.current = ref;
                    }}
                    canvasProps={{
                      width: 455,
                      height: 200,
                      className: Style.canvas,
                    }}
                  />
                )}

                {!loading && (
                  <div
                    className={Style.canvas_clear_button}
                    onClick={() => sigPad.current.clear()}
                  >
                    Clear
                  </div>
                )}
              </article>
              <div className={Style.footer}>
                <div className={Style.buttons}>
                  <button
                    className={Style.save}
                    onClick={() => {
                      if (!loading) {
                        finish();
                      }
                    }}
                    style={
                      loading
                        ? { cursor: 'default' }
                        : { cursor: 'pointer' }
                    }
                  >
                    {loading ? (
                      <img
                        src={loader}
                        alt="loader"
                        style={{ width: 25 }}
                      />
                    ) : (
                      'Finish'
                    )}
                  </button>
                </div>
              </div>
              <nav className={Style.nav}>
                {!loading && (
                  <Button
                    icon="log-out"
                    alignRight
                    color="dark"
                    size={16}
                    text="Skip"
                    className={Style.prev}
                    action={() => {
                      skip();
                    }}
                  />
                )}
              </nav>
            </>
          )}
        </section>
      </div>
    </Animate>
  );
}
